import { useEffect, useRef, useState } from 'react';

/**
 * Plan D-1. Replaces react-virtualized-auto-sizer, which §4 item 8 names but which
 * is the ONE dependency in that list absent from node_modules — adding it would
 * mean a registry fetch and a new transitive graph in the lockfile, where the other
 * three already resolve from the store at the declared versions.
 *
 * D9's substance is virtualization, and react-window delivers that. Only the
 * measuring package is declined.
 *
 * Guarded on `height > 0` by the caller: ResizeObserver fires after layout, so the
 * first paint reports 0 and a FixedSizeList of height 0 renders nothing and then
 * never re-measures.
 */
export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect;

      if (rect) {
        setSize({ width: rect.width, height: rect.height });
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}
